// PanelCxC.jsx - Panel de Cuentas por Cobrar (CXC)
// Misma estructura/CSS que ReporteCob.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp, TrendingDown, ChevronDown, Search,
  Users, Tag, RefreshCw, Filter, X, FilterX, Download,
  AlertTriangle, Wallet, Percent, FileText
} from 'lucide-react';
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LabelList
} from 'recharts';
import './DashboardBultos.css';
import { useQuery } from '../QueryContext';
import { useAuth } from '../AuthContext';
import FullScreenLoader from './FullScreenLoader';
import * as XLSX from 'xlsx';

/* eslint-disable react-hooks/exhaustive-deps */

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA', 'SIN SEGMENTO'];

const filterLabels = {
  CXC_ESTADO: "Estado",
  CXC_TIPO_DOC: "Tipo Doc.",
  CXC_MONEDA: "Moneda",
  CLI_REG_VERD: "Región",
  CXC_VENDEDOR_FINAL: "Vendedor",
  CLI_CANAL: "Canal",
  CLI_CANAL_REAL: "Canal Real",
  CLI_SEG_DES: "Segmento",
  CLI_TERRITORIO: "Territorio",
  CLI_PRIN: "Cliente Principal",
  CLIENTE_ID: "Código Cliente",
  CXC_NRO_DOC: "Nro. Documento",
};

const INITIAL_FILTERS = {
  CXC_ESTADO: [], CXC_TIPO_DOC: [], CXC_MONEDA: [],
  CLI_REG_VERD: [], CXC_VENDEDOR_FINAL: [], CLI_CANAL: [], CLI_CANAL_REAL: [],
  CLI_SEG_DES: [], CLI_TERRITORIO: [], CLI_PRIN: [], CLIENTE_ID: [],
  CXC_NRO_DOC: [],
};

const MES_A_NUM = {
  ENERO: 1, FEBRERO: 2, MARZO: 3, ABRIL: 4, MAYO: 5, JUNIO: 6,
  JULIO: 7, AGOSTO: 8, SEPTIEMBRE: 9, OCTUBRE: 10, NOVIEMBRE: 11, DICIEMBRE: 12,
};

// Orden lógico de estados para el gráfico de barras
const ESTADO_ORDER = {
  POR_VENCER: 1, VENCIDA_0_30: 2, VENCIDA_31_60: 3,
  VENCIDA_61_90: 4, VENCIDA_MAS_90: 5,
};

const ESTADO_LABEL = {
  POR_VENCER: 'Por Vencer',
  VENCIDA_0_30: 'Vencida 0-30d',
  VENCIDA_31_60: 'Vencida 31-60d',
  VENCIDA_61_90: 'Vencida 61-90d',
  VENCIDA_MAS_90: 'Vencida +90d',
};

const ESTADO_COLOR = {
  POR_VENCER: '#00ba52',
  VENCIDA_0_30: '#ffcc00',
  VENCIDA_31_60: '#ff9900',
  VENCIDA_61_90: '#ff4d4d',
  VENCIDA_MAS_90: '#8b0000',
};

// ─── Formateo ───
const parseNumericES = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const s = String(value).trim();
  if (!s) return 0;
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  if (hasComma && !hasDot) return Number(s.replace(/\./g, '').replace(',', '.'));
  return Number(s.replace(/,/g, ''));
};

const formatNumber = (num, decimals = 0) => {
  const n = parseNumericES(num);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('es-VE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatUSD = (num) => `$${formatNumber(num, 2)}`;
const formatBS = (num) => `Bs ${formatNumber(num, 2)}`;
const formatK = (num) => {
  const n = Number(num || 0);
  if (Math.abs(n) >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return Math.round(n);
};

const getMoraColor = (pct) => {
  const val = Number(pct || 0);
  if (val < 20) return '#00ba52';
  if (val < 40) return '#ffcc00';
  if (val < 60) return '#ff9900';
  return '#8b0000';
};

const COLORS = ['#1a2c4e', '#0068b3', '#00ba52', '#4f8cff', '#00e6b8', '#334767', '#8b5cf6', '#f59e0b'];

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.03) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 12;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x} y={y} fill="#cbd5e1" fontSize="10" fontWeight="600"
      textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

// Grid de columnas para la tabla CXC
const CXC_GRID = '1.4fr 1fr 1fr 0.9fr 1.4fr 1.3fr 0.8fr';

const WRAP_STYLE = {
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  lineHeight: 1.25,
};


// ══════════════════════════════════════════════════════════════
// MultiSelectFilter (idéntico a ReporteCob)
// ══════════════════════════════════════════════════════════════
const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues = [], onChange, onClearPill }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const totalOptions = options.length;
  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = totalOptions > 0 && selectedValues.length === totalOptions;

  const displayValue = allOptionsSelected
    ? 'Todos'
    : hasSelection
      ? (selectedValues.length === 1
        ? (options.find(opt => (opt.value || opt) === selectedValues[0])?.label ?? selectedValues[0])
        : `${selectedValues.length} Selec.`)
      : 'Todos';

  const showSearch = options.length > 12;
  const filteredOptions = options.filter(opt =>
    String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 200);

  const areAllSelected = filteredOptions.every(opt =>
    selectedValues.includes(opt.value || opt)
  );

  const handleSelectAll = () => {
    if (areAllSelected) {
      const newSelected = selectedValues.filter(val =>
        !filteredOptions.some(opt => (opt.value || opt) === val)
      );
      onChange(name, newSelected);
    } else {
      onChange(name, filteredOptions.map(opt => opt.value || opt));
    }
  };

  const handleSingleToggle = (optValue) => {
    const newVals = selectedValues.includes(optValue)
      ? selectedValues.filter(v => v !== optValue)
      : [...selectedValues, optValue];
    onChange(name, newVals);
  };

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input
                type="text" placeholder="Buscar..." value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)} autoFocus
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input type="checkbox" checked={areAllSelected} onChange={handleSelectAll} />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(optValue)}
                    onChange={() => handleSingleToggle(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => onClearPill?.(name)}>
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};


// ══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════
const PanelCxC = () => {
  const {
    getCxccobFilters,
    cxccobQueryKpis,
    cxccobQueryPorVencimiento,
    cxccobQueryTopClientes,
    cxccobQueryResumenVendedor,
    cxccobQueryTrendVencimiento,
    cxccobQueryDetalle,
    loading: apiLoading,
  } = useQuery();

  const { profile } = useAuth();

  const [localLoading, setLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [filtersReady, setFiltersReady] = useState(false);
  const filtersInitialized = useRef(false);

  // ─── KPIs ───
  const [kpis, setKpis] = useState({
    total_documentos: 0,
    clientes_unicos: 0,
    cartera_total_bs: 0,
    cartera_total_usd: 0,
    cartera_por_vencer_bs: 0,
    cartera_por_vencer_usd: 0,
    cartera_vencida_bs: 0,
    cartera_vencida_usd: 0,
    cartera_mas90_bs: 0,
    cartera_mas90_usd: 0,
    pct_mora: 0,
  });

  // ─── Datos ───
  const [porVencimiento, setPorVencimiento] = useState([]);
  const [topClientes, setTopClientes] = useState([]);
  const [resumenVendedor, setResumenVendedor] = useState([]);
  const [trendData, setTrendData] = useState([]);

  // ─── Tabla paginada ───
  const [detalleData, setDetalleData] = useState([]);
  const [detallePag, setDetallePag] = useState({
    page: 1, page_size: 100, total: 0, total_pages: 0,
  });
  const [sortBy, setSortBy] = useState('CXC_FEC_VENC');
  const [sortDir, setSortDir] = useState('asc');
  const [buscar, setBuscar] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // ─── Filtros ───
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const amountSuffix = currency.toLowerCase();
  const amountSortColumn = currency === 'USD' ? 'CXC_SALDOUSD' : 'CXC_SALDO';
  const formatMoney = value => currency === 'USD' ? formatUSD(value) : formatBS(value);

  const requestRefs = useRef({ kpis: 0, vencimiento: 0, top: 0, vendedor: 0, trend: 0, detalle: 0 });


  // ══════════════════════════════════════════════════════════════
  // CONFIG INICIAL
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    const loadConfig = async () => {
      if (filtersInitialized.current) return;
      setLocalLoading(true);
      try {
        const config = await getCxccobFilters();
        if (!config) throw new Error("Configuración de CxC no disponible.");

        const fullOptions = {};
        Object.keys(INITIAL_FILTERS).forEach(key => {
          const raw = config.availableOptions?.[key];
          if (Array.isArray(raw)) {
            fullOptions[key] = raw.map(item =>
              typeof item === 'string'
                ? item
                : { value: item?.value, label: item?.label || item?.value }
            ).filter(Boolean);
          } else {
            fullOptions[key] = [];
          }
        });

        setAvailableOptions(fullOptions);
        const allSegments = fullOptions.CLI_SEG_DES || [];
        const prefilteredSegments = allSegments
          .map(opt => opt?.value || opt)
          .filter(Boolean)
          .filter(segment =>
            !excludedSegments.some(ex => String(segment).toUpperCase().includes(ex.toUpperCase()))
          );

        setFilters({
          ...INITIAL_FILTERS,
          CLI_SEG_DES: prefilteredSegments,
        });

        filtersInitialized.current = true;
        setFiltersReady(true);
      } catch (err) {
        console.error('Error cargando config CxC:', err);
        filtersInitialized.current = true;
        setFiltersReady(true);
      } finally {
        setLocalLoading(false);
      }
    };
    loadConfig();
  }, []);


  // ══════════════════════════════════════════════════════════════
  // PAYLOAD
  // ══════════════════════════════════════════════════════════════
  const buildBasePayload = useCallback(() => {
    return {
      filters,
      user_info: profile
        ? { username: profile.username, cargo: profile.cargo }
        : null,
    };
  }, [filters, profile]);


  // ══════════════════════════════════════════════════════════════
  // FETCHERS
  // ══════════════════════════════════════════════════════════════
  const fetchKpis = useCallback(async () => {
    if (!filtersReady) return;
    const reqId = ++requestRefs.current.kpis;
    try {
      const res = await cxccobQueryKpis(buildBasePayload());
      if (reqId !== requestRefs.current.kpis) return;
      setKpis({
        total_documentos: parseInt(res?.total_documentos || 0, 10),
        clientes_unicos: parseInt(res?.clientes_unicos || 0, 10),
        cartera_total_bs: parseFloat(res?.cartera_total_bs || 0),
        cartera_total_usd: parseFloat(res?.cartera_total_usd || 0),
        cartera_por_vencer_bs: parseFloat(res?.cartera_por_vencer_bs || 0),
        cartera_por_vencer_usd: parseFloat(res?.cartera_por_vencer_usd || 0),
        cartera_vencida_bs: parseFloat(res?.cartera_vencida_bs || 0),
        cartera_vencida_usd: parseFloat(res?.cartera_vencida_usd || 0),
        cartera_mas90_bs: parseFloat(res?.cartera_mas90_bs || 0),
        cartera_mas90_usd: parseFloat(res?.cartera_mas90_usd || 0),
        pct_mora: parseFloat(res?.pct_mora || 0),
      });
    } catch (e) {
      console.error('❌ Error KPIs CxC:', e);
    }
  }, [buildBasePayload, cxccobQueryKpis, filtersReady]);

  const fetchPorVencimiento = useCallback(async () => {
    if (!filtersReady) return;
    const reqId = ++requestRefs.current.vencimiento;
    try {
      const res = await cxccobQueryPorVencimiento(buildBasePayload());
      if (reqId !== requestRefs.current.vencimiento) return;
      const arr = Array.isArray(res?.data) ? res.data : [];
      // Ordenar por bucket lógico
      const sorted = [...arr].sort(
        (a, b) => (ESTADO_ORDER[a.bucket] || 99) - (ESTADO_ORDER[b.bucket] || 99)
      );
      setPorVencimiento(sorted);
    } catch (e) {
      console.error('❌ Error por_vencimiento:', e);
      setPorVencimiento([]);
    }
  }, [buildBasePayload, cxccobQueryPorVencimiento, filtersReady]);

  const fetchTopClientes = useCallback(async () => {
    if (!filtersReady) return;
    const reqId = ++requestRefs.current.top;
    try {
      const res = await cxccobQueryTopClientes({ ...buildBasePayload(), limit: 15 });
      if (reqId !== requestRefs.current.top) return;
      setTopClientes(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('❌ Error top clientes CxC:', e);
      setTopClientes([]);
    }
  }, [buildBasePayload, cxccobQueryTopClientes, filtersReady]);

  const fetchResumenVendedor = useCallback(async () => {
    if (!filtersReady) return;
    const reqId = ++requestRefs.current.vendedor;
    try {
      const res = await cxccobQueryResumenVendedor(buildBasePayload());
      if (reqId !== requestRefs.current.vendedor) return;
      setResumenVendedor(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('❌ Error resumen vendedor CxC:', e);
      setResumenVendedor([]);
    }
  }, [buildBasePayload, cxccobQueryResumenVendedor, filtersReady]);

  const fetchTrend = useCallback(async () => {
    if (!filtersReady) return;
    const reqId = ++requestRefs.current.trend;
    try {
      const res = await cxccobQueryTrendVencimiento(buildBasePayload());
      if (reqId !== requestRefs.current.trend) return;
      setTrendData(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.error('❌ Error trend CxC:', e);
      setTrendData([]);
    }
  }, [buildBasePayload, cxccobQueryTrendVencimiento, filtersReady]);

  const fetchDetalle = useCallback(async (pageOverride = null) => {
    if (!filtersReady) return;
    const reqId = ++requestRefs.current.detalle;
    try {
      const payload = {
        ...buildBasePayload(),
        page: pageOverride ?? detallePag.page,
        page_size: detallePag.page_size,
        sort_by: sortBy,
        sort_dir: sortDir,
        buscar: buscar || null,
      };
      const res = await cxccobQueryDetalle(payload);
      if (reqId !== requestRefs.current.detalle) return;
      setDetalleData(Array.isArray(res?.data) ? res.data : []);
      setDetallePag({
        page: res?.page || 1,
        page_size: res?.page_size || 100,
        total: res?.total || 0,
        total_pages: res?.total_pages || 0,
      });
    } catch (e) {
      console.error('❌ Error detalle CxC:', e);
      setDetalleData([]);
    }
  }, [buildBasePayload, cxccobQueryDetalle, filtersReady, detallePag.page, detallePag.page_size, sortBy, sortDir, buscar]);


  // ══════════════════════════════════════════════════════════════
  // EFFECTS: cargar data cuando cambian filtros
  // ══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!filtersReady) return;
    const t = setTimeout(async () => {
      setQuerying(true);
      try {
        await Promise.all([
          fetchKpis(),
          fetchPorVencimiento(),
          fetchTopClientes(),
          fetchResumenVendedor(),
          fetchTrend(),
        ]);
      } finally {
        setQuerying(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [filters, filtersReady,
    fetchKpis, fetchPorVencimiento, fetchTopClientes, fetchResumenVendedor, fetchTrend]);

  // Detalle: cargar cuando cambia paginación / sort / buscar
  useEffect(() => {
    if (!filtersReady) return;
    const t = setTimeout(() => fetchDetalle(), 350);
    return () => clearTimeout(t);
  }, [filters, filtersReady, detallePag.page, sortBy, sortDir, buscar]);


  // ══════════════════════════════════════════════════════════════
  // HANDLERS
  // ══════════════════════════════════════════════════════════════
  const handleRefresh = useCallback(async () => {
    setQuerying(true);
    try {
      await Promise.all([
        fetchKpis(), fetchPorVencimiento(), fetchTopClientes(),
        fetchResumenVendedor(), fetchTrend(), fetchDetalle(),
      ]);
    } finally {
      setQuerying(false);
    }
  }, [fetchKpis, fetchPorVencimiento, fetchTopClientes, fetchResumenVendedor, fetchTrend, fetchDetalle]);

  const handleResetFilters = useCallback(() => {
    const allSegments = availableOptions.CLI_SEG_DES || [];
    const prefilteredSegments = allSegments
      .map(opt => opt?.value || opt)
      .filter(Boolean)
      .filter(segment =>
        !excludedSegments.some(ex => String(segment).toUpperCase().includes(ex.toUpperCase()))
      );

    setFilters({
      ...INITIAL_FILTERS,
      CLI_SEG_DES: prefilteredSegments,
    });
  }, [availableOptions]);

  const handleClearSingleFilter = useCallback((name) => {
    setFilters(prev => ({ ...prev, [name]: [] }));
  }, []);

  const handleSearchSubmit = useCallback((e) => {
    e?.preventDefault?.();
    setBuscar(searchInput.trim());
    setDetallePag(p => ({ ...p, page: 1 }));
  }, [searchInput]);

  const handleSort = useCallback((col) => {
    setSortBy(prev => {
      if (prev === col) {
        setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        return prev;
      }
      setSortDir('asc');
      return col;
    });
    setDetallePag(p => ({ ...p, page: 1 }));
  }, []);

  // ══════════════════════════════════════════════════════════════
  // EXPORT EXCEL
  // ══════════════════════════════════════════════════════════════
  const exportToExcel = (data, filename, type = 'generic') => {
    let rows = [];
    if (type === 'detalle') {
      rows = data.map(item => ({
        'Tipo Doc': item.CXC_TIPO_DOC || '',
        'Nro Doc': item.CXC_NRO_DOC || '',
        'Serie': item.CXC_N_SERIE || '',
        'Fec Emisión': item.CXC_FEC_EMISION || '',
        'Fec Venc': item.CXC_FEC_VENC || '',
        'Días Vencidos': item.CXC_DIASV || 0,
        'Estado': ESTADO_LABEL[item.CXC_ESTADO] || item.CXC_ESTADO || '',
        'Cliente ID': item.CLIENTE_ID || '',
        'Cliente Principal': item.CLI_PRIN || '',
        'Cliente Descripción': item.CLI_NOMBRE || '',
        'Región': item.CLI_REG_VERD || '',
        'Vendedor': item.CXC_VENDEDOR_FINAL || '',
        'Canal': item.CLI_CANAL || '',
        'Segmento': item.CLI_SEG_DES || '',
        'Moneda': item.CXC_MONEDA || '',
        'Tasa': item.CXC_TASA || 0,
        'Saldo Bs': Number(item.CXC_SALDO || 0).toFixed(2),
        'Saldo USD': Number(item.CXC_SALDOUSD || 0).toFixed(2),
        'Neto USD documento': Number(item.CXC_NETOUSD || 0).toFixed(2),
        'Monto Neto Bs': Number(item.CXC_MONTO_NET || 0).toFixed(2),
        'Base Imponible Bs': Number(item.CXC_BASEIMP || 0).toFixed(2),
        'IVA Bs': Number(item.CXC_IVA || 0).toFixed(2),
        'Marca': item.BI_MARCA || '',
        'Línea': item.BI_LINEA || '',
        'Proveedor': item.BI_PROVEEDOR || '',
      }));
    } else if (type === 'top_clientes') {
      rows = data.map(item => ({
        'Cliente ID': item.CLIENTE_ID || '',
        'Cliente Principal': item.cli_prin || '',
        'Región': item.region || '',
        'Vendedor': item.vendedor || '',
        'Canal': item.canal || '',
        'Documentos': item.documentos || 0,
        'Cartera USD': Number(item.cartera_usd || 0).toFixed(2),
        'Vencida USD': Number(item.vencida_usd || 0).toFixed(2),
        'Max Días': item.max_dias || 0,
      }));
    } else if (type === 'vendedores') {
      rows = data.map(item => ({
        'Vendedor': item.vendedor || '',
        'Región': item.region || '',
        'Clientes': item.clientes || 0,
        'Documentos': item.documentos || 0,
        'Cartera USD': Number(item.cartera_usd || 0).toFixed(2),
        'Vencida USD': Number(item.vencida_usd || 0).toFixed(2),
        'Por Vencer USD': Number(item.por_vencer_usd || 0).toFixed(2),
        '+90d USD': Number(item.mas_90_usd || 0).toFixed(2),
      }));
    }

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${filename}_${new Date().getTime()}.xlsx`);
  };


  // ══════════════════════════════════════════════════════════════
  // DATOS DERIVADOS
  // ══════════════════════════════════════════════════════════════
  // Chart de barras por vencimiento
  const barVencimientoData = useMemo(() => {
    return porVencimiento.map(item => ({
      bucket: item.bucket,
      name: ESTADO_LABEL[item.bucket] || item.bucket,
      Documentos: Number(item.documentos || 0),
      Clientes: Number(item.clientes || 0),
      Total: Number(item[`total_${amountSuffix}`] || 0),
      Pct: Number(item.pct_concentracion || 0),
      color: ESTADO_COLOR[item.bucket] || '#0068b3',
    }));
  }, [porVencimiento, amountSuffix]);

  // Pie top 5 deudores
  const pieTopDeudores = useMemo(() => {
    return [...topClientes]
      .sort((a, b) => Number(b[`cartera_${amountSuffix}`] || 0) - Number(a[`cartera_${amountSuffix}`] || 0))
      .slice(0, 5)
      .map(r => ({
        name: r.cli_prin || r.CLIENTE_ID || 'SIN NOMBRE',
        value: Number(r[`cartera_${amountSuffix}`] || 0),
      }))
      .filter(x => x.value > 0);
  }, [topClientes, amountSuffix]);

  // Bar chart vendedores top
  const barVendedorData = useMemo(() => {
    return [...resumenVendedor]
      .sort((a, b) => Number(b[`cartera_${amountSuffix}`] || 0) - Number(a[`cartera_${amountSuffix}`] || 0))
      .slice(0, 10)
      .map(v => ({
        name: (v.vendedor || 'SIN ASESOR').substring(0, 25),
        value: Number(v[`cartera_${amountSuffix}`] || 0),
        vencida: Number(v[`vencida_${amountSuffix}`] || 0),
      }));
  }, [resumenVendedor, amountSuffix]);

  // Trend: ordenar cronológicamente
  const chartTrendData = useMemo(() => {
    return [...trendData]
      .map(item => ({
        name: `${item.mes?.substring(0, 3)} ${item.anio}`,
        Cartera: Number(item[`cartera_${amountSuffix}`] || 0),
        Vencida: Number(item[`vencida_${amountSuffix}`] || 0),
        PorVencer: Number(item[`por_vencer_${amountSuffix}`] || 0),
        _anio: Number(item.anio || 0),
        _mes: MES_A_NUM[item.mes] || 0,
      }))
      .sort((a, b) => {
        if (a._anio !== b._anio) return a._anio - b._anio;
        return a._mes - b._mes;
      });
  }, [trendData, amountSuffix]);

  // ══════════════════════════════════════════════════════════════
  // KPI CARDS
  // ══════════════════════════════════════════════════════════════
  const moraColor = getMoraColor(kpis.pct_mora);

  const kpiCards = [
    {
      key: 'cartera',
      icon: <Wallet size={22} color="white" />,
      label: 'Cartera Total',
      value: formatMoney(kpis[`cartera_total_${amountSuffix}`]),
      sublabel: `${formatNumber(kpis.total_documentos)} docs · ${formatNumber(kpis.clientes_unicos)} clientes`,
      color: '#3b82f6',
      textColor: null,
    },
    {
      key: 'vencida',
      icon: <AlertTriangle size={22} color="white" />,
      label: 'Cartera Vencida',
      value: formatMoney(kpis[`cartera_vencida_${amountSuffix}`]),
      sublabel: `+90d: ${formatMoney(kpis[`cartera_mas90_${amountSuffix}`])}`,
      color: '#ff4d4d',
      textColor: '#ff4d4d',
    },
    {
      key: 'mora',
      icon: <Percent size={22} color="white" />,
      label: '% Mora',
      value: `${formatNumber(kpis.pct_mora, 1)}%`,
      sublabel: `Por vencer: ${formatMoney(kpis[`cartera_por_vencer_${amountSuffix}`])}`,
      color: moraColor,
      textColor: moraColor,
    },
  ];


  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };


  // ══════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════
  return (
    <motion.div
      className="dashboard-container"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6, ease: "easeInOut" }}
    >


      {(apiLoading || localLoading) && !filtersReady && (
        <FullScreenLoader message="Cargando datos de Cuentas por Cobrar..." />
      )}

      {querying && <div className="querying-overlay active" />}

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isMobile ? { duration: 0.2 } : { duration: 0.5, ease: "easeOut" }}
      >
        {/* ═══ HEADER ═══ */}
        <header className="header-flex">
          <div className="title-section">
            <h1 className="dashboard-title">Cuentas por Cobrar</h1>
            <div className="title-underline"></div>
          </div>

          <div className="date-selectors">
            <button className="refresh-button" onClick={handleRefresh} disabled={querying}>
              <RefreshCw size={16} className={querying ? "spin-anim" : ""} />
            </button>

            <div className="switch-toggle-group" role="group" aria-label="Moneda de visualización">
              {['USD', 'Bs'].map(unit => (
                <button
                  key={unit}
                  type="button"
                  className={`switch-toggle-btn ${currency === unit ? 'active' : ''}`}
                  aria-pressed={currency === unit}
                  onClick={() => setCurrency(unit)}
                >
                  {unit}
                </button>
              ))}
            </div>

            <button className="mobile-filter-btn" onClick={() => setShowMobileFilters(true)}>
              <Filter size={16} />
              <span>Filtros</span>
            </button>

          </div>
        </header>

        {/* ═══ BARRA RESUMEN ═══ */}
        <div className="working-days-bar">
          <div className="wd-item"><span>Docs:</span> <strong>{formatNumber(kpis.total_documentos)}</strong></div>
          <div className="wd-item"><span>Clientes:</span> <strong>{formatNumber(kpis.clientes_unicos)}</strong></div>
          <div className="wd-item"><span>Por Vencer:</span> <strong>{formatMoney(kpis[`cartera_por_vencer_${amountSuffix}`])}</strong></div>
          <div className="wd-separator"></div>
          <div className={`wd-item variation-item ${kpis.pct_mora >= 40 ? 'negative' : 'positive'}`}>
            {kpis.pct_mora >= 40 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            <strong>{kpis.pct_mora.toFixed(1)}%</strong>
            <span className="wd-label">(mora)</span>
          </div>
          <div className="wd-separator"></div>
        </div>

        {/* ═══ SIDEBAR FILTROS ═══ */}
        <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
          <div className="filters-sidebar-header">
            <h3>Filtros de CxC</h3>
            <div className="filters-header-actions">
              <button className="reset-filters-btn" onClick={handleResetFilters}>
                <FilterX size={16} />
              </button>
              <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="filters-grid" style={{ gap: '8px' }}>
            {Object.keys(filters)
              .map((key) => (
                <MultiSelectFilter
                  key={key}
                  label={filterLabels[key] || key}
                  name={key}
                  icon={key.includes('CLI') || key.includes('CXC_VENDEDOR') ? Users : (key === 'CXC_NRO_DOC' ? FileText : Tag)}
                  options={availableOptions[key] || []}
                  selectedValues={filters[key] || []}
                  onChange={(name, vals) => setFilters(prev => ({ ...prev, [name]: vals }))}
                  onClearPill={handleClearSingleFilter}
                />
              ))}
            <button className="filter-pill-button reset-filters" onClick={handleResetFilters}>
              <FilterX size={12} className="pill-icon" />
              <span className="pill-label">Limpiar</span>
            </button>
          </div>
        </div>
        {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

        {/* ═══ 3 KPI CARDS ═══ */}
        <div
          className="dashboard-indicators"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: 12,
          }}
        >
          {kpiCards.map(card => (
            <div key={card.key} className="indicator-card v-real">
              <div className="card-content">
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 44, height: 44, borderRadius: '50%',
                  background: card.color, marginBottom: 8,
                }}>
                  {card.icon}
                </div>
                <span className="indicator-label">{card.label}</span>
                <div className="indicator-row">
                  <span
                    className="indicator-value"
                    style={card.textColor ? { color: card.textColor } : undefined}
                  >
                    {card.value}
                  </span>
                </div>
                {card.sublabel && (
                  <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>{card.sublabel}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ═══ FILA 1 · BAR VENCIMIENTO + PIE TOP 5 DEUDORES ═══ */}
        <div className="charts-grid main-row" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s' }}>
          <div className="chart-container">
            <h3 className="chart-title">Concentración por Vencimiento</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={barVencimientoData} margin={{ top: 25, right: 15, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff20" />
                <XAxis
                  dataKey="name" axisLine={false} tickLine={false}
                  tick={{ fontSize: 9, fill: '#fff' }}
                />
                <YAxis
                  axisLine={false} tickLine={false} tickFormatter={formatK}
                  tick={{ fontSize: 9, fill: '#fff' }}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', borderRadius: 8, border: 'none', color: '#333' }}
                  formatter={(value, name) => {
                    if (name === 'Total') return formatMoney(value);
                    if (name === 'Documentos' || name === 'Clientes') return formatNumber(value);
                    return value;
                  }}
                />
                <Legend
                  verticalAlign="bottom" height={16} iconType="circle"
                  wrapperStyle={{
                    fontSize: '10px', backgroundColor: 'rgba(255,255,255,0.85)',
                    borderRadius: '12px', padding: '5px', width: '90%',
                    margin: '0 auto', left: '5%', bottom: 0,
                    display: 'flex', justifyContent: 'center', color: '#fff',
                  }}
                />
                <Bar dataKey="Total" radius={[4, 4, 0, 0]} barSize={32}>
                  {barVencimientoData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                  <LabelList
                    dataKey="Total" position="top" formatter={formatK}
                    style={{ fontSize: '10px', fill: '#fff', fontWeight: 'bold' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 4, marginTop: 8, fontSize: '0.65rem', opacity: 0.85,
            }}>
              {barVencimientoData.map((b, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ color: b.color, fontWeight: 700 }}>
                    {b.Pct.toFixed(1)}%
                  </div>
                  <div style={{ opacity: 0.75 }}>{formatNumber(b.Documentos)} docs</div>
                </div>
              ))}
            </div>
          </div>

          <div className="chart-container">
            <h3 className="chart-title">Top 5 Deudores</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={pieTopDeudores}
                  cx="50%" cy="45%"
                  innerRadius={40} outerRadius={70}
                  paddingAngle={4} dataKey="value"
                  labelLine={false} label={renderCustomizedLabel}
                >
                  {pieTopDeudores.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#fff" strokeWidth={0} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatMoney(value)} />
                <Legend
                  verticalAlign="bottom" height={30} iconType="circle"
                  wrapperStyle={{
                    fontSize: '8px', backgroundColor: 'rgba(255,255,255,0.85)',
                    borderRadius: '12px', padding: '5px', width: '90%',
                    margin: '0 auto', left: '5%', bottom: 0,
                    display: 'flex', justifyContent: 'center', color: '#fff',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ═══ FILA 2 · TREND MENSUAL ═══ */}
        <div className="chart-container" style={{ marginTop: '15px' }}>
          <h3 className="chart-title">Evolución de Cartera por Mes de Vencimiento</h3>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={chartTrendData} margin={{ top: 20, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff20" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: '#fff' }} />
              <YAxis axisLine={false} tickLine={false} tickFormatter={formatK} tick={{ fontSize: 9, fill: '#fff' }} />
              <Tooltip
                contentStyle={{ backgroundColor: 'white', borderRadius: 8, border: 'none', color: '#333' }}
                formatter={(value) => formatMoney(value)}
              />
              <Legend
                verticalAlign="bottom" height={16} iconType="circle"
                wrapperStyle={{
                  fontSize: '10px', backgroundColor: 'rgba(255,255,255,0.85)',
                  borderRadius: '12px', padding: '5px', width: '90%',
                  margin: '0 auto', left: '5%', bottom: 0,
                  display: 'flex', justifyContent: 'center', color: '#fff',
                }}
              />
              <Bar dataKey="PorVencer" stackId="a" fill="#00ba52" radius={[0, 0, 0, 0]} barSize={20} />
              <Bar dataKey="Vencida" stackId="a" fill="#ff4d4d" radius={[4, 4, 0, 0]} barSize={20} />
              <Line type="monotone" dataKey="Cartera" stroke="#4f8cff" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* ═══ FILA 3 · BAR VENDEDORES ═══ */}
        <div className="chart-container mini" style={{ marginTop: '15px' }}>
          <h3 className="chart-title">Top 10 Vendedores por Cartera</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={barVendedorData} layout="vertical" margin={{ top: 5, right: 50, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={false} stroke="#ffffff10" />
              <XAxis type="number" hide />
              <YAxis
                dataKey="name" type="category" axisLine={false} tickLine={false}
                tick={{ fontSize: 10, fill: '#fff' }} width={150}
              />
              <Tooltip
                cursor={{ fill: 'transparent' }}
                contentStyle={{ backgroundColor: 'white', borderRadius: 8, border: 'none', color: '#333' }}
                formatter={(value) => formatMoney(value)}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={16}>
                {barVendedorData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
                <LabelList
                  dataKey="value" position="right" formatter={(v) => formatK(v)}
                  style={{ fill: '#cbd5e1', fontSize: 10, fontWeight: 'bold' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ═══ FILA 4 · TABLA DETALLE PAGINADA ═══ */}
        <div className="detail-table-container2" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s' }}>
          <div className="chart-container mini table-fixed-height" style={{ flex: 2 }}>
            <div className="table-header-container">
              <h3 className="chart-title">
                <FileText size={14} style={{ display: 'inline', marginRight: 6 }} />
                Detalle de Cuentas por Cobrar
                <span style={{ marginLeft: 8, fontSize: '0.7rem', opacity: 0.6 }}>
                  ({formatNumber(detallePag.total)} documentos)
                </span>
              </h3>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: 4 }}>
                  <input
                    type="text" value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Buscar doc / cliente..."
                    style={{
                      padding: '4px 8px', fontSize: '0.75rem', borderRadius: 6,
                      border: '1px solid rgba(255,255,255,0.15)',
                      background: 'rgba(0,0,0,0.25)', color: '#fff', minWidth: 180,
                    }}
                  />
                  <button type="submit" className="export-table-btn" style={{ padding: '4px 8px' }}>
                    <Search size={12} />
                  </button>
                </form>
                <button
                  className="export-table-btn"
                  onClick={() => exportToExcel(detalleData, 'Detalle_CxC', 'detalle')}
                >
                  <Download size={12} />
                </button>
              </div>
            </div>

            <div className="minimal-table-wrapper">
              {/* HEADER */}
              <div className="table-header5" style={{ gridTemplateColumns: CXC_GRID }}>
                <div className="col-name" style={WRAP_STYLE}>Documento</div>
                <div className="col-data" onClick={() => handleSort('CLI_NOMBRE')} style={{ cursor: 'pointer' }}>
                  Cliente {sortBy === 'CLI_NOMBRE' && (sortDir === 'asc' ? '↑' : '↓')}
                </div>
                <div className="col-data" onClick={() => handleSort('CXC_FEC_VENC')} style={{ cursor: 'pointer' }}>
                  Vencimiento {sortBy === 'CXC_FEC_VENC' && (sortDir === 'asc' ? '↑' : '↓')}
                </div>
                <div className="col-data" onClick={() => handleSort('CXC_DIASV')} style={{ cursor: 'pointer' }}>
                  Días {sortBy === 'CXC_DIASV' && (sortDir === 'asc' ? '↑' : '↓')}
                </div>
                <div className="col-name" style={WRAP_STYLE}>Vendedor / Región</div>
                <div className="col-data" onClick={() => handleSort(amountSortColumn)} style={{ cursor: 'pointer' }}>
                  Saldo {currency} {sortBy === amountSortColumn && (sortDir === 'asc' ? '↑' : '↓')}
                </div>
                <div className="col-status">Estado</div>
              </div>

              {/* BODY */}
              <div className="table-body5">
                {detalleData.length === 0 && (
                  <div style={{ padding: 20, textAlign: 'center', opacity: 0.5 }}>
                    {querying ? 'Cargando...' : 'Sin documentos'}
                  </div>
                )}

                {detalleData.map((row, idx) => {
                  const estado = row.CXC_ESTADO || 'POR_VENCER';
                  const color = ESTADO_COLOR[estado] || '#888';
                  const dias = Number(row.CXC_DIASV || 0);

                  return (
                    <div
                      className="table-row5"
                      key={`${row.CXC_NRO_DOC}-${idx}`}
                      style={{ gridTemplateColumns: CXC_GRID, fontSize: '0.72rem' }}
                    >
                      <div className="col-name" style={WRAP_STYLE}>
                        <div style={{ fontWeight: 700 }}>
                          {row.CXC_N_SERIE || 'XXX'}-{row.CXC_NRO_DOC}
                        </div>
                        <div style={{ fontSize: '0.62rem', opacity: 0.6 }}>
                          {row.CXC_TIPO_DOC} · {row.CXC_FEC_EMISION}
                        </div>
                      </div>
                      <div className="col-data" style={WRAP_STYLE} title={row.CLI_NOMBRE}>
                        <div style={{ fontWeight: 600 }}>
                          {row.CLI_PRIN || 'SIN CLIENTE'}
                        </div>
                        <div style={{ fontSize: '0.62rem', opacity: 0.65 }}>
                          {row.CLIENTE_ID || ''}
                        </div>
                      </div>
                      <div className="col-data">
                        <div>{row.CXC_FEC_VENC || '—'}</div>
                      </div>
                      <div className="col-data bold" style={{
                        color: dias > 90 ? '#ff4d4d' : dias > 30 ? '#ffcc00' : '#00ba52'
                      }}>
                        {dias > 0 ? `+${dias}` : dias}
                      </div>
                      <div className="col-name" style={WRAP_STYLE}>
                        <div style={{ fontSize: '0.68rem' }}>{row.CXC_VENDEDOR_FINAL || '—'}</div>
                        <div style={{ fontSize: '0.6rem', opacity: 0.6 }}>{row.CLI_REG_VERD || ''}</div>
                      </div>
                      <div className="col-data bold" style={{ textAlign: 'right' }}>
                        {formatMoney(currency === 'USD' ? row.CXC_SALDOUSD : row.CXC_SALDO)}
                      </div>
                      <div className="col-status">
                        <div
                          className="status-dot"
                          style={{ width: 8, height: 8, backgroundColor: color }}
                          title={ESTADO_LABEL[estado] || estado}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* PAGINACIÓN */}
              {detallePag.total_pages > 1 && (
                <div style={{
                  display: 'flex', justifyContent: 'center', gap: 8,
                  padding: '8px 0', borderTop: '1px solid rgba(255,255,255,0.1)',
                }}>
                  <button
                    className="export-table-btn"
                    disabled={detallePag.page <= 1}
                    onClick={() => setDetallePag(p => ({ ...p, page: p.page - 1 }))}
                  >
                    ← Anterior
                  </button>
                  <span style={{ alignSelf: 'center', fontSize: '0.75rem', opacity: 0.8 }}>
                    Página {detallePag.page} de {detallePag.total_pages}
                  </span>
                  <button
                    className="export-table-btn"
                    disabled={detallePag.page >= detallePag.total_pages}
                    onClick={() => setDetallePag(p => ({ ...p, page: p.page + 1 }))}
                  >
                    Siguiente →
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <footer className="status-footer">
          {querying
            ? "🔄 SINCRONIZANDO CUENTAS POR COBRAR..."
            : `💰 CUENTAS POR COBRAR · ${formatNumber(detallePag.total)} documentos · Cartera: ${formatMoney(kpis[`cartera_total_${amountSuffix}`])} · Mora: ${formatNumber(kpis.pct_mora, 1)}%`}
        </footer>
      </motion.div>
    </motion.div>
  );
};

export default PanelCxC;